import { css, html, LitElement, nothing } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { t } from 'src/i18n';
import type { DEditSelectText } from 'src/library/editors/components/d-edit-select-text';

import 'src/library/elements/d-section';
import 'src/library/editors/components/d-edit-select-text';
import 'src/library/editors/elements/d-select-dropdown';
import 'src/library/elements/d-smooth-resize';
import 'src/library/elements/d-label';
import 'src/content/d-confirmation-on-occurrences';
import { levenshteinDistance } from 'src/utilities/text';
import type { ModelForEntityName } from './model-for-entity-name';
import type { Occurrence } from '../library/lists/occurrence';
import type { SelectDropdownOption } from '../library/editors/elements/select-dropdown-option';
import type { CreateEntityInput } from '../layout/parts/create-entity-input';
import type { Suggestion } from '../library/editors/components/suggestion';

/**
 *
 */
@customElement('d-edit-entity-name')
export class DEditEntityName extends LitElement {
  static readonly styles = css`
    :host {
      display: block;
    }
    .actions {
      text-align: right;
      margin-right: -8px;
    }
    .interview-suggestions {
      border-top: 1px solid var(--borderColor);
      border-bottom: 1px solid var(--borderColor);
      padding: 12px 0;
    }
    .interview-suggestions ul {
      list-style-type: none;
      margin: 6px 0 0 0;
      padding: 0;
    }
    .interview-suggestions li {
      padding: 4px 0;
      color: var(--themeColor);
      cursor: pointer;
    }
    @media (hover: hover) {
      .interview-suggestions li:hover {
        color: black;
      }
    }
  `;
  @property({ type: Boolean })
  autofocus = false;
  @property({ type: Boolean })
  newItem = false;
  @property({ type: Array })
  assets: string[] = [];
  @property({ type: String })
  entityType = 'event';
  @property({ type: String })
  placeholder = '';
  @property({ type: String })
  parentHref = '';
  @property({ type: String })
  name = '';
  @property({ type: String })
  originalName = '';
  @property({ type: String })
  currentEntityUuid = '';
  @property({ type: String })
  masterEntityUuid = '';
  @property({ type: Array })
  models: ModelForEntityName[] = [];
  @property({ type: Array })
  taskOptions: SelectDropdownOption[] = [];
  @property({ type: Boolean })
  disableTaskSelection = false;
  @property({ type: Number })
  contentStickyTop = 0;
  @property({ type: Boolean })
  nameConfirmedOnInterview = false;
  @property({ type: Boolean })
  nameConfirmedOnOccurrence = false;
  @property({ type: Boolean })
  bypassConfirmations = false;
  @property({ type: String })
  newEmployeeSuggestionText = t('editEntityName.newEmployeeInterviewSuggestion');

  @query('#name')
  nameInput!: DEditSelectText;
  @state()
  private nameSelected = false;

  private get suggestions(): Suggestion[] {
    const suggestions = this.models.map((e) => {
      return { name: e.name, type: e.type };
    });
    suggestions.push({ name: this.newEmployeeSuggestionText, type: 'meetings' });
    return suggestions;
  }

  private get taskOptionsAndNone() {
    return [{ value: '', text: t('editEntityName.taskNone') }].concat(this.taskOptions);
  }

  private get sameNameTask() {
    return this.taskOptions.find((t) => {
      return t.text === this.name;
    });
  }

  private get model() {
    return this.models.find((m) => {
      return m.name === this.name;
    });
  }

  private get otherFutureOccurrences(): Occurrence[] {
    if (this.model && this.model.futureOccurrences) {
      return this.model.futureOccurrences.filter((o) => {
        return o.occurrenceUuid !== this.currentEntityUuid;
      });
    }
    return [];
  }

  private get showConfirmationOnInterview() {
    if (this.bypassConfirmations) {
      return false;
    }
    return this.nameSelected && this.inteviewSuggestions.length > 0 && !this.nameConfirmedOnInterview;
  }

  private get showConfirmationOnOccurrences() {
    if (this.bypassConfirmations) {
      return false;
    }
    return (
      this.nameSelected &&
      this.otherFutureOccurrences.length > 0 &&
      !this.showConfirmationOnInterview &&
      !this.nameConfirmedOnOccurrence
    );
  }

  private get interviews(): ModelForEntityName[] {
    return this.models.filter((m) => {
      return m.name.includes('Medarbeidersamtale med ');
    });
  }

  private get inteviewSuggestions(): ModelForEntityName[] {
    if (
      this.interviews
        .map((i) => {
          return i.name;
        })
        .includes(this.name)
    ) {
      return [];
    }
    const nameParts = this.name.toLowerCase().split(' ');
    const matchingInteviews = this.interviews.filter((i) => {
      let result = false;
      const interviewNameParts = i.name.replace('Medarbeidersamtale med ', '').split(' ');
      interviewNameParts.forEach((iPart) => {
        nameParts.forEach((part) => {
          if (levenshteinDistance(part, iPart) < 2) {
            result = true;
          }
        });
      });
      return result;
    });
    if (
      (this.name.toLowerCase().includes('medarbeider') || this.name.toLowerCase().includes('samtale')) &&
      matchingInteviews.length > 0
    ) {
      return matchingInteviews;
    }
    let interviewNoEmployees = false;
    nameParts.forEach((part) => {
      if (levenshteinDistance(part, 'medarbeidersamtale') < 4 && matchingInteviews.length === 0) {
        interviewNoEmployees = true;
      }
    });
    if (interviewNoEmployees) {
      return this.interviews;
    }
    return [];
  }

  onNameFocused() {
    this.nameSelected = false;
    this.nameConfirmedOnInterview = false;
    this.nameConfirmedOnOccurrence = false;
    this.dispatchEvent(
      new CustomEvent('name-focused', {
        bubbles: true,
        composed: true,
      }),
    );
  }

  onNameChanged(e: CustomEvent<{ value: string }>) {
    this.name = e.detail.value;
  }

  onNameSelected(e: CustomEvent<{ value: string }>) {
    if (e.detail.value === this.newEmployeeSuggestionText) {
      this.addNewEmployeeForInterview();
    } else {
      this.name = e.detail.value;
      this.setNameSelected();
    }
  }

  setNameSelected() {
    this.nameSelected = true;
    this.nameInput.dismissOptions();
    if (this.sameNameTask) {
      this.masterEntityUuid = this.sameNameTask.value;
    } else if (this.model) {
      this.masterEntityUuid = this.model.masterEntityUuid;
    }
    if (!this.showConfirmationOnInterview && !this.showConfirmationOnOccurrences) {
      this.done();
    }
    setTimeout(() => {
      this.nameInput.blur();
    }, 0);
  }

  onTaskSelected(e: CustomEvent<{ value: string }>) {
    if (e.detail.value) {
      this.masterEntityUuid = e.detail.value;
      this.name =
        this.taskOptions.find((t) => {
          return t.value === e.detail.value;
        })?.text || '';
      this.setNameSelected();
    }
  }

  onClickNext(e) {
    e.stopPropagation();
    if (!e.target.disabled) {
      this.setNameSelected();
    }
  }

  onCancelOnInterview() {
    setTimeout(() => {
      this.nameInput.focusInput();
    }, 0);
  }

  onConfirmOnOccurrences() {
    this.nameConfirmedOnOccurrence = true;
    this.done();
  }

  private onOccurrenceLinkClick(e: CustomEvent<{ href: string }>) {
    const href = e.detail.href;
    // Trigger the same behavior as "Nei, avbryt" button
    this.onCancel();
    // Navigate to the clicked occurrence
    this.dispatchEvent(
      new CustomEvent('navigate', {
        bubbles: true,
        composed: true,
        detail: { href },
      }),
    );
  }

  selectInterview(name) {
    this.name = name;
    this.setNameSelected();
  }

  addNewEmployeeForInterview() {
    this.dispatchEvent(
      new CustomEvent<CreateEntityInput>('add-new-employee-for-interview', {
        bubbles: true,
        composed: true,
      }),
    );
  }

  render() {
    return html`
      <d-section>
        <d-edit-select-text
          id="name"
          select-on-focus
          label="${t('editEntityName.nameLabel')}"
          mark-if-empty
          .autofocus=${this.autofocus}
          placeholder="${this.placeholder}"
          .value=${this.name}
          .items=${this.suggestions}
          @focus=${() => this.onNameFocused()}
          @value-changed=${(e: CustomEvent<{ value: string }>) => this.onNameChanged(e)}
          @value-selected=${(e) => this.onNameSelected(e)}
        ></d-edit-select-text>
      </d-section>
      ${this.taskOptions.length && !this.disableTaskSelection
        ? html`
            <d-section topless>
              <d-select-dropdown
                label="${t('editEntityName.linkedRoutineLabel')}"
                .labelAction=${this.masterEntityUuid === '' ? '' : t('editEntityName.removeRoutine')}
                label-action-plain
                .options=${this.taskOptionsAndNone}
                .value=${this.masterEntityUuid}
                @value-changed=${(e: CustomEvent<{ value: string }>) => this.onTaskSelected(e)}
                @label-action=${() => (this.masterEntityUuid = '')}
              >
              </d-select-dropdown>
            </d-section>
          `
        : nothing}
      <d-smooth-resize>
        ${!this.nameSelected && !this.nameConfirmedOnOccurrence && !this.bypassConfirmations
          ? html`
              <d-section topless>
                <div class="actions">
                  <d-action @click=${() => this.onCancel()}>${t('common:cancel')}</d-action>
                  <d-action ?disabled=${!this.name} @click=${(e) => this.onClickNext(e)}
                    >${t('editEntityName.next')}</d-action
                  >
                </div>
              </d-section>
            `
          : nothing}
      </d-smooth-resize>
      <d-smooth-resize>
        ${this.showConfirmationOnInterview
          ? html`
              <div class="interview-suggestions">
                <d-label label="${t('editEntityName.createInterviewPrompt')}"></d-label>
                <ul>
                  ${this.inteviewSuggestions.map((s) => {
                    return html`<li @click=${() => this.selectInterview(s.name)}>${s.name}</li>`;
                  })}
                  <li @click=${() => this.addNewEmployeeForInterview()}>${t('editEntityName.addNewEmployee')}</li>
                  <li @click=${() => this.onCancelOnInterview()}>${t('common:cancel')}</li>
                </ul>
              </div>
            `
          : nothing}
      </d-smooth-resize>
      <d-smooth-resize>
        ${this.showConfirmationOnOccurrences
          ? html`
              <d-confirmation-on-occurrences
                .contentStickyTop=${this.contentStickyTop}
                .newItem=${this.newItem}
                .entityType=${this.entityType}
                .occurrences=${this.otherFutureOccurrences}
                .assets=${this.assets}
                .parentHref=${this.parentHref}
                .entityName=${this.name}
                @confirm=${() => this.onConfirmOnOccurrences()}
                @cancel=${() => this.onCancel()}
                @occurrence-link-click=${(e: CustomEvent<{ href: string }>) => this.onOccurrenceLinkClick(e)}
              ></d-confirmation-on-occurrences>
            `
          : nothing}
      </d-smooth-resize>
    `;
  }

  private onCancel() {
    this.name = this.originalName;
    this.nameConfirmedOnInterview = true;
    this.nameConfirmedOnOccurrence = true;
    this.dispatchEvent(
      new CustomEvent('cancel', {
        bubbles: true,
        composed: true,
      }),
    );
    setTimeout(() => {
      this.nameInput.blur();
    }, 0);
  }

  private done() {
    this.dispatchEvent(
      new CustomEvent('done', {
        bubbles: true,
        composed: true,
        detail: {
          name: this.name,
          masterEntityUuid: this.masterEntityUuid,
        },
      }),
    );
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-edit-entity-name': DEditEntityName;
  }
}
